import axios from 'axios';
import { defineComponent } from 'vue';
import constants from '../shared/constants';

export default defineComponent({
    data() {
        return {
            constants,
            filtersOpen: false,
        };
    },
    mounted() {
        this.filtersOpen = this.hasActiveFilterQueryParams();
        this.updateResetFiltersVisibility();
        this.syncFiltersPanelHeight();
    },
    methods: {
        getListElement(): HTMLElement | null {
            return this.$el as HTMLElement | null;
        },
        getFormElement(): HTMLFormElement | null {
            const rootElement = this.getListElement();

            if (!rootElement) return null;

            if (rootElement instanceof HTMLFormElement) {
                return rootElement;
            }

            return rootElement.querySelector('form');
        },
        getResultsElement(): HTMLElement | null {
            return document.querySelector(constants.targets.volunteerGroupList.results);
        },
        getResetFiltersElement(): HTMLElement | null {
            return this.getListElement()?.querySelector(constants.targets.volunteerGroupList.resetFilters) ?? null;
        },
        hasActiveFilterQueryParams(): boolean {
            const urlParams = new URLSearchParams(window.location.search);

            return constants.volunteerGroupList.filterKeys.some((key) => {
                const value = urlParams.get(key);

                return typeof value === 'string' && value.length > 0;
            });
        },
        getFiltersPanelElement(): HTMLElement | null {
            return this.getListElement()?.querySelector(constants.targets.volunteerGroupList.filtersPanel) ?? null;
        },
        syncFiltersPanelHeight(): void {
            const filtersPanelElement = this.getFiltersPanelElement();

            if (!filtersPanelElement) return;

            filtersPanelElement.style.maxHeight = this.filtersOpen ? `${filtersPanelElement.scrollHeight}px` : '0';
        },
        toggleFilters(): void {
            this.filtersOpen = !this.filtersOpen;

            this.$nextTick(() => {
                this.syncFiltersPanelHeight();
            });
        },
        getQueryString(): string {
            const form = this.getFormElement();

            if (!form) return '';

            const urlParams = new URLSearchParams();
            const formData = new FormData(form);
            const multiValueEntries = new Map<string, string[]>();

            constants.volunteerGroupList.filterKeys.forEach((key) => urlParams.delete(key));
            urlParams.delete(constants.queryParams.page);

            formData.forEach((value, key) => {
                if (!key || key === constants.queryParams.bringToFront || typeof value !== 'string' || !value) return;

                const existingValues = multiValueEntries.get(key) ?? [];

                existingValues.push(value);
                multiValueEntries.set(key, existingValues);
            });

            multiValueEntries.forEach((values, key) => {
                urlParams.set(key, values.join(','));
            });

            return urlParams.toString();
        },
        updateResetFiltersVisibility(): void {
            const resetFiltersElement = this.getResetFiltersElement();

            if (!resetFiltersElement) return;

            const hasActiveFilters = this.getQueryString().length > 0;

            resetFiltersElement.classList.toggle(constants.classes.hidden, !hasActiveFilters);
        },
        async refreshResults(): Promise<void> {
            const form = this.getFormElement();
            const currentResultsElement = this.getResultsElement();

            if (!form || !currentResultsElement) return;

            const queryString = this.getQueryString();
            const requestUrl = queryString ? `${form.action}?${queryString}` : form.action;
            let response;

            try {
                response = await axios.get<string>(requestUrl, {
                    responseType: 'text',
                });
            } catch {
                window.location.href = requestUrl;
                return;
            }

            const parser = new DOMParser();
            const documentResult = parser.parseFromString(response.data, 'text/html');
            const nextResultsElement = documentResult.querySelector<HTMLElement>(constants.targets.volunteerGroupList.results);

            if (!nextResultsElement) {
                window.location.href = requestUrl;
                return;
            }

            currentResultsElement.replaceWith(nextResultsElement);
            history.replaceState({}, '', requestUrl);
        },
        updateFilter(event: Event): void {
            const target = event.target as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement | null;
            const timeout = target?.type === 'checkbox' ? constants.volunteerGroupList.changeTimeoutMs : 0;

            this.updateResetFiltersVisibility();

            window.setTimeout(async () => {
                this.updateResetFiltersVisibility();
                this.syncFiltersPanelHeight();
                await this.refreshResults();
            }, timeout);
        },
    },
});
